import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { ReactComponent as IconPlay } from "../../../assets/icons/icon-play-outline.svg";
import Visibility from "../content/Visibility";
import ContentComplete from "../content/ContentComplete";
import ContentLink from "../../general/ContentLink";
import Carousel from "../../general/Carousel";
import { getImageSrcRelOrAbsolute } from "../../../utils";
import { selectUserProgress } from "../../../features/content/contentSlice";

export function DurationIndicator({ duration }) {
  if (!duration || isNaN(duration)) return null;

  function formatDuration(seconds) {
    const roundedSeconds = Math.round(seconds);
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const remainingSeconds = roundedSeconds % 60;

    return hours > 0
      ? `${hours}:${minutes.toString().padStart(2, "0")}:${remainingSeconds
          .toString()
          .padStart(2, "0")}`
      : `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }

  return (
    <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
      {formatDuration(duration)}
    </div>
  );
}

export function ProgressBar({ progress, duration, primaryColor }) {
  if (!progress || !duration || isNaN(progress) || isNaN(duration)) return null;

  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black bg-opacity-20">
      <div
        className="h-full"
        style={{
          width: `${(progress / duration) * 100}%`,
          backgroundColor: primaryColor
        }}
      />
    </div>
  );
}

export function ContentItem({ content, collectionSlug, fixedWidth, innerRef, groupSlug }) {
  const { platformData } = useSelector(state => state.frontend);
  const { currentUser } = useSelector(state => state.auth);
  const userProgress = useSelector(selectUserProgress(content.id));
  const imageUrl = getImageSrcRelOrAbsolute(
    content.featuredImage || platformData.heroImage || platformData.logo,
    "?tr=w-384,h-216,q-80"
  );

  return (
    <li
      className={`mt-2 mb-6 scale-100 hover:scale-105 overflow-hidden  rounded-md transition duration-200 ease-in-out ${
        fixedWidth ? " w-48 sm:w-80" : ""
      }`}
      ref={innerRef}
    >
      <ContentLink
        isInternal={content.type === "internallink"}
        to={
          content.type === "internallink"
            ? content.contentURI
            : groupSlug
            ? `/groups/${groupSlug}/${collectionSlug || "viewcontent"}/${content.id}`
            : `/${collectionSlug || "viewcontent"}/${content.id}`
        }
      >
        <div className="relative">
          <div className="relative overflow-hidden rounded-md">
            <img className="h-full w-full object-cover" src={imageUrl} alt={content.title} />
            <DurationIndicator duration={content.duration} />
            <ProgressBar
              progress={userProgress?.progress}
              duration={content.duration}
              primaryColor={platformData.primaryColor}
            />
          </div>

          <div className="absolute flex  transition-all text-center bg-transparent bg-opacity-50 bg-black justify-center font-bold text-white opacity-0 hover:opacity-100 items-center w-full h-full top-0">
            {"article" === content.type ||
            "download" === content.type ||
            "link" === content.type ? null : (
              <>
                <IconPlay className="w-10  h-10 mr-2" /> Watch Now
              </>
            )}
          </div>

          <Visibility currentUser={currentUser} content={content} absolute={true} />
          <div className="absolute top-2 left-2 ">
            <ContentComplete
              content={content}
              buttonClass={"text-xs py-0 px-2"}
              platformData={platformData}
              readOnly
            />
          </div>
        </div>
        <div className="sm:mt-4">
          <span className="mt-2 font-sm font-bold  line-clamp-2   leading-6 sm:text-xl  ">
            {content.title}
          </span>
        </div>
        <div className="sm:mt-2">
          {content.User?.name && (
            <span className="text-gray-700 text-xs mt-6 leading-3 font-semibold  mr-4 sm:text-base sm:leading-5">
              {content.User?.name}
            </span>
          )}

          <span className="text-gray-700 text-xs mt-2 italic opacity-50 leading-3 font-weight-normal sm:text-base sm:leading-5">
            {moment(content.publishedDate || content.createdAt).format("MMM D, YYYY")}
          </span>
        </div>
      </ContentLink>
    </li>
  );
}

function CollectionHeader({ collection, overrideTitle, darkColor }) {
  return (
    <Link to={`/${collection.slug}`} className="flex">
      <h1 className="collection-title text-xl leading-none sm:text-3xl font-bold">
        {overrideTitle ? overrideTitle : collection.name}{" "}
      </h1>
      <span
        className="font-light ml-3 base  py-1 px-2 bg-black bg-opacity-10 text-sm rounded-full  "
        style={{ color: `${darkColor}` }}
      >
        {collection.contentTotal} Post{collection.contentTotal > 1 && "s"}
      </span>
    </Link>
  );
}

const sortAndFilterCollection = (collection, filterContent) => {
  return (collection.Contents || []).filter(content => {
    if (content.id === filterContent) return null;
    return true;
  });
};

const SecondaryCollection = ({
  overrideTitle,
  filterContent,
  collection,
  groupSlug,
  paddingClass = "px-10",
  usePlatformTheme = true // prevent Collection slide from being dark the Today tab in groups
}) => {
  const { platformData } = useSelector(state => state.frontend);

  const renderItem = useCallback(
    ({ item, innerRef }) => (
      <ContentItem
        groupSlug={groupSlug}
        collectionSlug={collection.slug}
        content={item}
        innerRef={innerRef}
        fixedWidth
      />
    ),
    [collection.slug, groupSlug]
  );
  const renderHeader = useCallback(
    () => (
      <CollectionHeader
        darkColor={usePlatformTheme && platformData.darkColor}
        overrideTitle={overrideTitle}
        collection={collection}
      />
    ),
    [overrideTitle, collection, platformData.darkColor, usePlatformTheme]
  );

  const items = sortAndFilterCollection(collection, filterContent);

  if (!collection.Contents) return null;

  return (
    <div
      className="bg-white pt-0 sm:pt-8 pb-0 sm:pb-8"
      style={{
        backgroundColor: usePlatformTheme && platformData.lightColor,
        color: usePlatformTheme && platformData.darkColor
      }}
    >
      <span id="firstcollection"></span>
      {items?.length > 0 && (
        <Carousel
          items={items}
          showMoreLink={collection.contentTotal > 20 && `/${collection.slug}`}
          renderItem={renderItem}
          renderHeader={renderHeader}
          paddingClass={paddingClass}
        />
      )}
    </div>
  );
};

export default SecondaryCollection;
